'use client';

import { forwardRef, useCallback, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableState } from '../../hooks/use-controllable-state';
import { Slot } from '../slot';
import { ChipGroupContext } from './context/chip-group.context';
import { chipGroupRecipe } from './styles/chip-group.styles';
import type { ChipGroupPropsInterface } from './types/chip-group.types';

/**
 * `ChipGroup` is a component that renders a group of chips.
 *
 * @since 10.2.0
 */
export const ChipGroup = forwardRef<HTMLDivElement, ChipGroupPropsInterface>(
  (props: ChipGroupPropsInterface, forwardedRef) => {
    const {
      asChild,
      className,
      elevated,
      children,
      css: cssProp,
      selected,
      defaultSelected,
      onSelectionChange,
      multiple = false,
      disabled = false,
      verticalSpacing = 'default',
      role = 'group',
      ...rest
    } = props;

    const [selectedState, setSelectedState] = useControllableState({
      prop: selected,
      defaultProp: defaultSelected,
      onChange: onSelectionChange,
    });

    const isSelected = useCallback(
      (value: string): boolean => {
        if (!selectedState) return false;

        if (multiple && Array.isArray(selectedState)) {
          return selectedState.includes(value);
        }

        return selectedState === value;
      },
      [selectedState, multiple],
    );

    const handleSelection = useCallback(
      (value: string) => {
        if (multiple) {
          const currentArray = Array.isArray(selectedState) ? selectedState : [];
          const isCurrentlySelected = currentArray.includes(value);

          const newSelection = isCurrentlySelected
            ? currentArray.filter((v) => v !== value)
            : [...currentArray, value];

          setSelectedState(newSelection);
        } else {
          // Single selection - toggle off if clicking the same value
          const newSelection = selectedState === value ? undefined : value;
          setSelectedState(newSelection);
        }
      },
      [selectedState, setSelectedState, multiple],
    );

    const contextValue = useMemo(
      () => ({
        selected: selectedState,
        setSelected: setSelectedState,
        multiple,
        disabled,
        elevated,
        isSelected,
        handleSelection,
      }),
      [selectedState, setSelectedState, multiple, disabled, elevated, isSelected, handleSelection],
    );

    const styles = css(chipGroupRecipe.raw({ verticalSpacing }), cssProp);

    const Component = asChild ? Slot : 'div';

    return (
      <ChipGroupContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="ChipGroup"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          role={role}
          {...rest}
        >
          {children}
        </Component>
      </ChipGroupContext.Provider>
    );
  },
);

ChipGroup.displayName = 'ChipGroup';
